import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type TileLayerKey = 'street' | 'satellite';

export interface MapSettings {
  tileLayer: TileLayerKey;
  showPins: boolean;
  showPolygons: boolean;
  showParcels: boolean;
  /** Show ParcelNo labels on parcels (only takes effect when showParcels is on). */
  showParcelNo: boolean;
  /** Show FPA boundary outlines (staff-only). */
  showBoundaries: boolean;
  /** Forecast-only: wind direction arrow overlay. */
  showWind: boolean;
  /** Hydrants and private water sources overlay. */
  showWaterSources: boolean;
  /**
   * Per-tenant list of enabled boundary IDs. A missing entry for a tenant
   * means "show all" (sensible default for fresh users). An empty array
   * means "show none". Keyed by tenant ID as a string for JSON-friendliness.
   */
  selectedBoundaryIds: Record<string, number[]>;
}

const STORAGE_KEY = 'map-settings:v1';

/** Stable identity so the `update` callback isn't rebuilt every render. */
const EMPTY_LOCAL_KEYS: Array<keyof MapSettings> = [];

const DEFAULTS: MapSettings = {
  tileLayer: 'street',
  showPins: true,
  showPolygons: true,
  showParcels: false,
  showParcelNo: true,
  showBoundaries: true,
  showWind: false,
  showWaterSources: false,
  selectedBoundaryIds: {},
};

function read(): MapSettings {
  if (typeof window === 'undefined') return DEFAULTS;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<MapSettings>;
    return {
      tileLayer: parsed.tileLayer === 'satellite' ? 'satellite' : 'street',
      showPins: parsed.showPins !== false,
      showPolygons: parsed.showPolygons !== false,
      showParcels: parsed.showParcels === true,
      // Default-on: existing localStorage entries without this key get true.
      showParcelNo: parsed.showParcelNo !== false,
      showBoundaries: parsed.showBoundaries !== false,
      showWind: parsed.showWind === true,
      showWaterSources: parsed.showWaterSources === true,
      selectedBoundaryIds:
        parsed.selectedBoundaryIds && typeof parsed.selectedBoundaryIds === 'object'
          ? (parsed.selectedBoundaryIds as Record<string, number[]>)
          : {},
    };
  } catch {
    return DEFAULTS;
  }
}

export interface MapSettingsOptions {
  /** Seed values for the keys listed in `local`. */
  overrides?: Partial<MapSettings>;
  /**
   * Keys this page owns privately: seeded from `overrides` on mount, and
   * never read from nor written to localStorage. Use for a per-page default
   * that must not leak to other maps — e.g. the forecast map starts with
   * pins off, but toggling them there must not turn them off on /wildfires.
   */
  local?: Array<keyof MapSettings>;
}

function pickLocal(
  overrides: Partial<MapSettings>,
  keys: Array<keyof MapSettings>,
): Partial<MapSettings> {
  const out: Partial<MapSettings> = {};
  for (const k of keys) {
    if (k in overrides) {
      // Index-assignment across a union of value types needs the cast; the
      // key is already constrained to keyof MapSettings.
      (out as Record<string, unknown>)[k] = overrides[k];
    }
  }
  return out;
}

/**
 * Shared map preferences (layer + visibility toggles) persisted to
 * localStorage. Use in any map view; the shape is identical so settings
 * stay synced across pages.
 *
 * Pass `options` to carve out page-local keys — see `MapSettingsOptions`.
 * `options` is read once on mount, so callers may pass an inline literal,
 * though hoisting it to a module constant documents the intent better.
 */
export function useMapSettings(options?: MapSettingsOptions) {
  const [persisted, setPersisted] = useState<MapSettings>(read);

  // Frozen on first render: re-reading it every render would let an inline
  // object literal silently reset the page-local layer on each re-render.
  const optionsRef = useRef(options);
  const localKeys = optionsRef.current?.local ?? EMPTY_LOCAL_KEYS;
  const [localState, setLocalState] = useState<Partial<MapSettings>>(() =>
    pickLocal(optionsRef.current?.overrides ?? {}, localKeys),
  );

  // Re-read once on mount in case another tab updated it before we rendered.
  useEffect(() => {
    setPersisted(read());
  }, []);

  const settings = useMemo<MapSettings>(
    () => ({ ...persisted, ...localState }),
    [persisted, localState],
  );

  const update = useCallback((patch: Partial<MapSettings>) => {
    const localPatch: Partial<MapSettings> = {};
    const persistPatch: Partial<MapSettings> = {};
    for (const key of Object.keys(patch) as Array<keyof MapSettings>) {
      const target = localKeys.includes(key) ? localPatch : persistPatch;
      (target as Record<string, unknown>)[key] = patch[key];
    }

    if (Object.keys(localPatch).length > 0) {
      setLocalState((prev) => ({ ...prev, ...localPatch }));
    }
    if (Object.keys(persistPatch).length === 0) return;

    setPersisted((prev) => {
      const next = { ...prev, ...persistPatch };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* storage full / blocked */
      }
      return next;
    });
  }, [localKeys]);

  /**
   * Returns the set of enabled boundary IDs for the given tenant.
   * - `null` means "show all" (the sensible default for fresh users).
   * - Otherwise an explicit Set the caller can filter against.
   */
  const getEnabledBoundaryIds = useCallback(
    (tenantId: number | null): Set<number> | null => {
      if (tenantId == null) return null;
      const stored = settings.selectedBoundaryIds[String(tenantId)];
      if (!stored) return null;
      return new Set(stored);
    },
    [settings.selectedBoundaryIds],
  );

  /** Toggle a single boundary on/off for a tenant. */
  const toggleBoundary = useCallback(
    (tenantId: number, boundaryId: number, allBoundaryIds: number[]) => {
      setPersisted((prev) => {
        const key = String(tenantId);
        // If nothing's stored yet, "all visible" is the implicit state — so
        // a toggle-off should explicitly list the others.
        const current =
          prev.selectedBoundaryIds[key] ??
          allBoundaryIds.slice();
        const set = new Set(current);
        if (set.has(boundaryId)) {
          set.delete(boundaryId);
        } else {
          set.add(boundaryId);
        }
        const next: MapSettings = {
          ...prev,
          selectedBoundaryIds: {
            ...prev.selectedBoundaryIds,
            [key]: Array.from(set).sort((a, b) => a - b),
          },
        };
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        } catch {
          /* storage full / blocked */
        }
        return next;
      });
    },
    [],
  );

  /** Reset a tenant's selection back to "show all" (i.e. remove the entry). */
  const resetBoundarySelection = useCallback((tenantId: number) => {
    setPersisted((prev) => {
      const key = String(tenantId);
      if (!(key in prev.selectedBoundaryIds)) return prev;
      const { [key]: _omit, ...rest } = prev.selectedBoundaryIds;
      void _omit;
      const next: MapSettings = { ...prev, selectedBoundaryIds: rest };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* storage full / blocked */
      }
      return next;
    });
  }, []);

  return {
    settings,
    update,
    getEnabledBoundaryIds,
    toggleBoundary,
    resetBoundarySelection,
  };
}
